import "@/App.css";
import React, { Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingBookButton from "@/components/site/FloatingBookButton";
import CookieBanner from "@/components/site/CookieBanner";
import PhoneCompletionModal from "@/components/site/PhoneCompletionModal";
import TermsGateModal from "@/components/site/TermsGateModal";
import WaiverResignBanner from "@/components/site/WaiverResignBanner";
import ScrollToTop from "@/components/site/ScrollToTop";
import Home from "@/pages/Home";
import BookGateway from "@/pages/BookGateway";
import Membership from "@/pages/Membership";
import Coaches from "@/pages/Coaches";
import CheckIn from "@/pages/CheckIn";
import Login from "@/pages/Login";
import Account from "@/pages/Account";
import Admin from "@/pages/Admin";
import AuthCallback from "@/pages/AuthCallback";
import GoogleCallback from "@/pages/GoogleCallback";
import DevLogin from "@/pages/DevLogin";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import WaiverQR from "@/pages/WaiverQR";
import MembershipComingSoon from "@/pages/MembershipComingSoon";
import NotFound from "@/pages/NotFound";
import { AuthProvider } from "@/lib/auth";
import { SettingsProvider, useSettings } from "@/lib/settings";

const ScanToCheckIn = React.lazy(() => import("@/pages/ScanToCheckIn"));

const PageLoader = () => (
  <div className="min-h-[40vh] flex items-center justify-center bg-spike-surface">
    <Loader2 className="w-6 h-6 animate-spin text-spike-blue-light" />
  </div>
);

// Wraps a route element and only renders it when the matching feature flag is enabled.
function FeatureRoute({ flag, children, fallback }) {
  const settings = useSettings();
  if (settings.loading) return null;
  if (!settings[flag]) return fallback || <NotFound />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: detect OAuth callback synchronously to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Header />
      <WaiverResignBanner />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/book" element={<BookGateway />} />
          <Route path="/membership" element={<FeatureRoute flag="show_membership" fallback={<MembershipComingSoon />}><Membership /></FeatureRoute>} />
          <Route path="/coaches" element={<Coaches />} />
          <Route path="/checkin" element={<CheckIn />} />
          <Route path="/checkin/:bookingId" element={<CheckIn />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/auth/google" element={<GoogleCallback />} />
          <Route path="/dev-login" element={<DevLogin />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/waiver-qr" element={<WaiverQR />} />
          <Route
            path="/scantocheckin"
            element={
              <Suspense fallback={<PageLoader />}>
                <ScanToCheckIn />
              </Suspense>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
      <FloatingBookButton />
      <Footer />
      <CookieBanner />
      <PhoneCompletionModal />
      <TermsGateModal />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-spike-surface text-spike-text font-exo antialiased">
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <AppRouter />
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#0A101C",
            border: "1px solid rgba(31,111,170,0.5)",
            color: "#E8E8E8",
            fontFamily: "'Exo 2', sans-serif",
          },
        }}
      />
    </div>
  );
}

export default App;
